import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";

import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListSubheader from "@material-ui/core/ListSubheader";
import Divider from "@material-ui/core/Divider";
import { Badge } from "@material-ui/core";
import Tooltip from "@material-ui/core/Tooltip";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import HistoryOutlinedIcon from "@material-ui/icons/HistoryOutlined";
import StarBorderOutlinedIcon from "@material-ui/icons/StarBorderOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import QuestionAnswerOutlinedIcon from "@material-ui/icons/QuestionAnswerOutlined";
import AssignmentTurnedInOutlinedIcon from "@material-ui/icons/AssignmentTurnedInOutlined";
import AssignmentOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import BusinessOutlinedIcon from "@material-ui/icons/BusinessOutlined";
import AccountCircleOutlinedIcon from "@material-ui/icons/AccountCircleOutlined";
import ForumOutlinedIcon from "@material-ui/icons/ForumOutlined";
import LocalOfferOutlinedIcon from "@material-ui/icons/LocalOfferOutlined";
import SubjectOutlinedIcon from "@material-ui/icons/SubjectOutlined";
import BuildOutlinedIcon from "@material-ui/icons/BuildOutlined";
import DialpadOutlinedIcon from "@material-ui/icons/DialpadOutlined";
import StorageOutlinedIcon from "@material-ui/icons/StorageOutlined";
import BugReportOutlinedIcon from "@material-ui/icons/BugReportOutlined";
import ViewColumnOutlinedIcon from "@material-ui/icons/ViewColumn";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";

import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { InternalChatNotificationsContext } from "../context/InternalChatNotifications/InternalChatNotificationsContext";
import { Can } from "../components/Can";
import useCompanySipEnabled from "../hooks/useCompanySipEnabled";
import api from "../services/api";
import { getTickets } from "../services/ticketQueries";
import openSocket from "../services/socket-io";

const useStyles = makeStyles((theme) => ({
  navItem: {
    position: "relative",
    margin: "3px 12px",
    borderRadius: 14,
    paddingTop: 10,
    paddingBottom: 10,
    transition:
      "background-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.06)",
      transform: "translateX(2px)",
    },
  },
  navItemActive: {
    background:
      "linear-gradient(90deg, rgba(20, 184, 166, 0.30) 0%, rgba(15, 118, 110, 0.14) 100%)",
    boxShadow: "inset 0 0 0 1px rgba(45, 212, 191, 0.22)",
    "&:hover": {
      background:
        "linear-gradient(90deg, rgba(20, 184, 166, 0.34) 0%, rgba(15, 118, 110, 0.18) 100%)",
    },
    "&::before": {
      content: '""',
      position: "absolute",
      left: -12,
      top: 9,
      bottom: 9,
      width: 3,
      borderRadius: "0 3px 3px 0",
      backgroundColor: "#2dd4bf",
    },
  },
  navItemCompact: {
    justifyContent: "center",
    alignItems: "center",
    width: 52,
    minWidth: 52,
    height: 52,
    padding: "0 !important",
    margin: "6px auto",
  },
  navIcon: {
    minWidth: 38,
    color: "#cddde3",
  },
  navIconCompact: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 0,
    width: 24,
    margin: 0,
    "& .MuiBadge-root": {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    },
  },
  navIconActive: {
    color: "#ffffff",
  },
  navText: {
    color: "#cddde3",
    "& span": {
      fontWeight: 500,
      color: "#cddde3",
      fontSize: "0.9rem",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  navTextActive: {
    color: "#ffffff",
    "& span": {
      fontWeight: 700,
      color: "#ffffff",
    },
  },
  navTextHidden: {
    display: "none",
  },
  subheader: {
    color: "#54707d",
    fontWeight: 700,
    letterSpacing: "0.10em",
    textTransform: "uppercase",
    fontSize: 11,
    marginTop: 8,
    lineHeight: "32px",
  },
  subheaderHidden: {
    display: "none",
  },
}));

function ListItemLink(props) {
  const { icon, primary, to, className, compact } = props;
  const classes = useStyles();
  const location = useLocation();
  const isActive =
    to === "/"
      ? location.pathname === "/"
      : location.pathname === to || location.pathname.startsWith(`${to}/`);

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  const item = (
    <ListItem
      button
      component={renderLink}
      className={`${classes.navItem} ${isActive ? classes.navItemActive : ""} ${compact ? classes.navItemCompact : ""} ${className || ""}`.trim()}
    >
      {icon ? (
        <ListItemIcon
          className={`${classes.navIcon} ${isActive ? classes.navIconActive : ""} ${compact ? classes.navIconCompact : ""}`.trim()}
        >
          {icon}
        </ListItemIcon>
      ) : null}
      <ListItemText
        primary={primary}
        className={`${classes.navText} ${isActive ? classes.navTextActive : ""} ${compact ? classes.navTextHidden : ""}`.trim()}
      />
    </ListItem>
  );

  return <li>{compact ? <Tooltip title={primary} placement="right">{item}</Tooltip> : item}</li>;
}

const MainListItems = (props) => {
  const classes = useStyles();
  const { drawerClose, onOpenProfile, drawerOpen = true } = props;
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const { sipEnabled: isCompanySipEnabled } = useCompanySipEnabled();
  const { unreadCount } = useContext(InternalChatNotificationsContext);
  const [connectionWarning, setConnectionWarning] = useState(false);
  const [ticketUnreadCount, setTicketUnreadCount] = useState(0);
  const [ticketPendingCount, setTicketPendingCount] = useState(0);
  const compact = !drawerOpen;

  const refreshTicketUnreadCount = useCallback(async () => {
    if (!user?.id || user.profile === "superadmin") {
      setTicketUnreadCount(0);
      setTicketPendingCount(0);
      return;
    }

    try {
      const commonParams = {
        pageNumber: 1,
        status: "open",
        countOnly: "true",
        withUnreadMessages: "true",
        assignedOnlyUnread: "true"
      };

      const [privateResponse, groupResponse, pendingResponse] = await Promise.all([
        getTickets({
          ...commonParams,
          ticketType: "private"
        }),
        getTickets({
          ...commonParams,
          ticketType: "group"
        }),
        // Aguardando nao passa por assignedOnlyUnread: chamado sem dono nunca
        // casaria com userId, e e justamente o chamado sem dono que ninguem
        // esta vendo chegar. Sem withUnreadMessages tambem, para o numero
        // bater com a aba Aguardando - um pendente que alguem abriu para
        // espiar zera unreadMessages mas continua parado esperando alguem
        // assumir. O recorte de fila/perfil e o mesmo da listagem.
        getTickets({
          pageNumber: 1,
          status: "pending",
          countOnly: "true"
        })
      ]);

      setTicketUnreadCount(
        Number(privateResponse.data.count || 0) + Number(groupResponse.data.count || 0)
      );
      setTicketPendingCount(Number(pendingResponse.data.count || 0));
    } catch (error) {
      setTicketUnreadCount(0);
      setTicketPendingCount(0);
    }
  }, [user?.id, user?.profile]);

  useEffect(() => {
    refreshTicketUnreadCount();
  }, [refreshTicketUnreadCount]);

  useEffect(() => {
    if (!user?.id || user.profile === "superadmin") {
      return undefined;
    }

    const socket = openSocket();
    let refreshTimeoutId;

    const scheduleRefresh = () => {
      if (refreshTimeoutId) {
        window.clearTimeout(refreshTimeoutId);
      }

      refreshTimeoutId = window.setTimeout(() => {
        refreshTicketUnreadCount();
      }, 220);
    };

    socket.on("connect", () => {
      socket.emit("joinNotification");
      socket.emit("joinTickets", "open");
      scheduleRefresh();
    });

    socket.on("appMessage", data => {
      if (data?.action === "create" && !data?.message?.read) {
        scheduleRefresh();
      }
    });

    socket.on("ticket", data => {
      if (["update", "updateUnread", "create", "delete"].includes(data?.action)) {
        scheduleRefresh();
      }
    });

    return () => {
      if (refreshTimeoutId) {
        window.clearTimeout(refreshTimeoutId);
      }
      socket.disconnect();
    };
  }, [refreshTicketUnreadCount, user?.id, user?.profile]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  // O badge soma duas coisas diferentes de proposito: quem olha o menu quer
  // saber se ha algo esperando por ele, nao a origem do numero. O tooltip abre
  // a conta em "nao lidas" e "aguardando".
  const ticketBadgeCount = ticketUnreadCount + ticketPendingCount;
  const ticketBadgeTooltip = [
    ticketUnreadCount > 0
      ? i18n.t("mainDrawer.listItems.attendanceBadgeUnread", {
          total: ticketUnreadCount
        })
      : null,
    ticketPendingCount > 0
      ? i18n.t("mainDrawer.listItems.attendanceBadgePending", {
          total: ticketPendingCount
        })
      : null
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div onClick={drawerClose}>
      {user.profile === "superadmin" ? (
        <Can
          role={user.profile}
          perform="drawer-superadmin-items:view"
          yes={() => (
            <>
              <ListItemLink
                to="/companies"
                primary={i18n.t("mainDrawer.listItems.companies")}
                icon={<BusinessOutlinedIcon />}
                compact={compact}
              />
              <ListItemLink
                to="/users"
                primary={i18n.t("mainDrawer.listItems.users")}
                icon={<PeopleAltOutlinedIcon />}
                compact={compact}
              />
              <ListItemLink
                to="/announcements"
                primary={i18n.t("mainDrawer.listItems.announcements")}
                icon={<ReportProblemOutlinedIcon />}
                compact={compact}
              />
              <ListItemLink
                to="/backups"
                primary={i18n.t("mainDrawer.listItems.backups")}
                icon={<StorageOutlinedIcon />}
                compact={compact}
              />
              <ListItemLink
                to="/debug-logs"
                primary={i18n.t("mainDrawer.listItems.debugLogs")}
                icon={<BugReportOutlinedIcon />}
                compact={compact}
              />
              <Tooltip title={i18n.t("mainDrawer.listItems.profile")} placement="right" disableHoverListener={!compact}>
                <ListItem
                  button
                  onClick={onOpenProfile}
                  className={`${classes.navItem} ${compact ? classes.navItemCompact : ""}`.trim()}
                >
                <ListItemIcon className={`${classes.navIcon} ${compact ? classes.navIconCompact : ""}`.trim()}>
                  <AccountCircleOutlinedIcon />
                </ListItemIcon>
                <ListItemText
                  primary={i18n.t("mainDrawer.listItems.profile")}
                  className={`${classes.navText} ${compact ? classes.navTextHidden : ""}`.trim()}
                />
                </ListItem>
              </Tooltip>
            </>
          )}
        />
      ) : (
        <>
          <ListItemLink
            to="/"
            primary={i18n.t("mainDrawer.listItems.dashboard")}
            icon={<DashboardOutlinedIcon />}
            compact={compact}
          />
          <ListItemLink
            to="/tickets"
            primary={i18n.t("mainDrawer.listItems.attendance")}
            icon={
              <Tooltip title={ticketBadgeTooltip} placement="right">
                <Badge badgeContent={ticketBadgeCount} color="secondary">
                  <WhatsAppIcon />
                </Badge>
              </Tooltip>
            }
            compact={compact}
          />
          <ListItemLink
            to="/internal-chat"
            primary={i18n.t("mainDrawer.listItems.internalChat")}
            icon={
              <Badge badgeContent={unreadCount} color="secondary">
                <ForumOutlinedIcon />
              </Badge>
            }
            compact={compact}
          />
          <ListItemLink
            to="/internal-tickets"
            primary={i18n.t("mainDrawer.listItems.internalTickets")}
            icon={<AssignmentOutlinedIcon />}
            compact={compact}
          />

          <ListItemLink
            to="/contacts"
            primary={i18n.t("mainDrawer.listItems.contacts")}
            icon={<ContactPhoneOutlinedIcon />}
            compact={compact}
          />
          <ListItemLink
            to="/quickAnswers"
            primary={i18n.t("mainDrawer.listItems.quickAnswers")}
            icon={<QuestionAnswerOutlinedIcon />}
            compact={compact}
          />
        </>
      )}
      <Can
        role={user.profile}
        perform="drawer-admin-items:view"
        yes={() => (
          <>
            <Divider />
            <ListSubheader
              inset
              className={`${classes.subheader} ${compact ? classes.subheaderHidden : ""}`.trim()}
            >
              {i18n.t("mainDrawer.listItems.administration")}
            </ListSubheader>
            <ListItemLink
              to="/attendances"
              primary={i18n.t("mainDrawer.listItems.attendances")}
              icon={<AssignmentTurnedInOutlinedIcon />}
              compact={compact}
            />
            <ListItemLink
              to="/monitoramento"
              primary={i18n.t("mainDrawer.listItems.monitoring")}
              icon={<ViewColumnOutlinedIcon />}
              compact={compact}
            />
            <ListItemLink
              to="/api-tools"
              primary="API Tools"
              icon={<BuildOutlinedIcon />}
              compact={compact}
            />
            <ListItemLink
              to="/company"
              primary={i18n.t("mainDrawer.listItems.company")}
              icon={<BusinessOutlinedIcon />}
              compact={compact}
            />
            <ListItemLink
              to="/connections"
              primary={i18n.t("mainDrawer.listItems.connections")}
              icon={
                <Badge badgeContent={connectionWarning ? "!" : 0} color="error">
                  <SyncAltIcon />
                </Badge>
              }
              compact={compact}
            />
            <ListItemLink
              to="/queues"
              primary={i18n.t("mainDrawer.listItems.queues")}
              icon={<AccountTreeOutlinedIcon />}
              compact={compact}
            />
            <ListItemLink
              to="/users"
              primary={i18n.t("mainDrawer.listItems.users")}
              icon={<PeopleAltOutlinedIcon />}
              compact={compact}
            />
            {isCompanySipEnabled && (
              <ListItemLink
                to="/extensions"
                primary={i18n.t("mainDrawer.listItems.extensions")}
                icon={<DialpadOutlinedIcon />}
                compact={compact}
              />
            )}
            <ListItemLink
              to="/tags"
              primary={i18n.t("mainDrawer.listItems.tags")}
              icon={<LocalOfferOutlinedIcon />}
              compact={compact}
            />
            <ListItemLink
              to="/ticket-subjects"
              primary={i18n.t("mainDrawer.listItems.ticketSubjects")}
              icon={<SubjectOutlinedIcon />}
              compact={compact}
            />
            <ListItemLink
              to="/ratings"
              primary="Avaliações"
              icon={<StarBorderOutlinedIcon />}
              compact={compact}
            />
            <ListItemLink
              to="/audit"
              primary="Auditoria"
              icon={<HistoryOutlinedIcon />}
              compact={compact}
            />
            <ListItemLink
              to="/settings"
              primary={i18n.t("mainDrawer.listItems.settings")}
              icon={<SettingsOutlinedIcon />}
              compact={compact}
            />
          </>
        )}
      />
    </div>
  );
};

export default MainListItems;
